<template>
  <TableCustomMini
    ref="filesTable"
    class="table-custom-mini"
    :fields="fields"
    :hasArchiveButton="store.getters.hasFileEditorAccess"
    :hasCreateButton="store.getters.hasFileCreatorAccess"
    :hasDeleteButton="store.getters.hasFileAdminAccess"
    :hasDownloadButton="store.getters.hasFileReaderAccess"
    :items="fileItems"
    :itemType="t('general__data__files')"
    :selectedId="selectedId"
    @archiveItem="emits('archiveFile')"
    @deleteItem="emits('deleteFile')"
    @downloadItem="emits('downloadFile')"
    @loadMore="emits('loadMoreFiles')"
    @menuClosed="piniaStore.setSelectedFile(null)"
    @menuOpened="setFullFileById($event)"
  >
    <template #preview="{ item }">
      <ButtonText
        v-if="checkValidFileType(item)"
        class="table-custom-mini__button"
        :class="{ selected: item.id === selectedId }"
        :iconLeft="iconArrowUpRight"
        size="extra-small"
        :whiteContent="false"
        @onClicked="showModalPreviewFile(item)"
      ></ButtonText>
      <span v-else style="width: 24px"></span>
    </template>
  </TableCustomMini>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import ButtonText from '@/design-system/atoms/buttons/ButtonText.vue'
import TableCustomMini, { TableItem } from '@/design-system/molecules/TableCustomMini.vue'
import { Attachment } from '@/services/attachments.service'
import iconArrowUpRight from '@/design-system/assets/icons/Icon-Arrow-up-right.svg'

type Props = {
  fields: any[]
  items?: Attachment[]
}
const props = defineProps<Props>()
const emits = defineEmits(['archiveFile', 'deleteFile', 'downloadFile', 'loadMoreFiles'])
const { t } = useI18n()

const piniaStore = useGeneralStore()
const store = useStore()

const filesTable = ref()
const fileItems = computed<TableItem[]>(() => {
  if (!props.items) return []
  return props.items.map((item) => {
    return {
      id: item.attachmentId,
      name: item.name,
      entityId: item.entityId,
      fileType: item.fileType,
      active: true // Currently, we expect all files in the list to be active (not archived or deleted)
    }
  })
})

const currentOverlayModalPinia = computed(() => {
  return piniaStore.currentOverlayModalPinia
})
const currentOverlayModalOnModalPinia = computed(() => {
  return piniaStore.currentOverlayModalOnModalPinia
})
const selectedId = computed(() => {
  return piniaStore.selectedFile?.attachmentId ?? ''
})
const showOverlayModalPinia = computed(() => {
  return piniaStore.showOverlayModalPinia
})
const showOverlayModalOnModalPinia = computed(() => {
  return piniaStore.showOverlayModalOnModalPinia
})

function checkValidFileType(item: TableItem) {
  return (
    item.fileType === 'gif' ||
    item.fileType === 'jpg' ||
    item.fileType === 'jpeg' ||
    item.fileType === 'pdf' ||
    item.fileType === 'png' ||
    item.fileType === 'svg'
  )
}
function setFullFileById(id: string) {
  if (!props.items) return
  let file = props.items.find((file) => file.attachmentId === id)
  if (file) piniaStore.setSelectedFile(file)
}
function showModalPreviewFile(item: TableItem) {
  if (!props.items) return
  let file = props.items.find((file) => file.attachmentId === item.id)
  if (!file) return
  filesTable.value.closeMenu()
  piniaStore.setSelectedFile(file)
  if (showOverlayModalPinia.value && currentOverlayModalPinia.value === 'editTicket') {
    // For tickets, modal on modal was a joint decision on prod/tech review 2024-08-19
    piniaStore.setCurrentOverlayModalOnModalPinia('previewTicketFilePinia')
    piniaStore.setShowOverlayModalOnModalPinia(true)
    return
  }
  piniaStore.setCurrentOverlayModalPinia('previewFilePinia')
  piniaStore.setShowOverlayModalPinia(true)
}

watch(showOverlayModalPinia, () => {
  if (!showOverlayModalPinia.value) {
    piniaStore.setSelectedFile(null)
  }
})
watch(showOverlayModalOnModalPinia, () => {
  if (
    !showOverlayModalOnModalPinia.value &&
    currentOverlayModalOnModalPinia.value === 'previewTicketFilePinia'
  ) {
    piniaStore.setSelectedFile(null)
  }
})
watch(
  () => piniaStore.selectedFile,
  () => {
    if (!piniaStore.selectedFile) {
      filesTable.value.closeMenu()
    }
  }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.table-custom-mini {
  flex: 1 1 auto;
  min-height: calc((32px * 6) + 16px); // 6 rows + 16px padding
  padding: 16px variables.$padding-main-body;
  padding-top: 0px;
  z-index: 1;
  &__button {
    padding: 0;
    &.selected {
      background-color: variables.$color-primary;
      :deep(img) {
        filter: brightness(100);
      }
    }
    &:hover:not(.selected) {
      background-color: rgba(205, 222, 254, 0.2);
    }
  }
}
</style>
